import { View } from 'react-native';
import { router } from 'expo-router';
import { CircleCheck, Clock, FileText, TriangleAlert } from 'lucide-react-native';
import { colors, formatLongDate, formatMinutes, formatShortDate, formatTime, parseDate, pick, reasonLabels, statusLabels, summarize, tasksInPlan } from '@hogar/shared';
import { Button, Card, Dot, RowLink, Screen, SectionTitle, Txt, useT } from '../../components/ui';
import { statusColor, statusTextColor } from '../../components/StatusSheet';
import { toRestock, useHouse } from '../../lib/house';
import { crewSlice, useCrew } from '../../lib/crew';
import { CrewSwitch } from '../../components/CrewSwitch';

export default function Reports() {
  const { t, lang } = useT();
  const snap = useHouse();
  const crew = useCrew();
  // The house and the garden each have their own day and report.
  const { tasks, rooms, products, staff, reports, schedule } = crewSlice(snap, crew);
  const { today, todayDate } = snap;
  // The day is closed once its report is saved (staff finished, or the end time passed).
  const closed = reports[0]?.date === today.date;
  const past = reports.filter((r) => r.date !== today.date);
  const results = today.results;
  const todays = tasksInPlan(snap.plans[today.date]?.[crew], today.weekday, tasks);
  const s = summarize(todays, results);
  const couldNot = todays.filter((tk) => results[tk.id]?.status === 'could_not');
  const open = todays.filter((tk) => !results[tk.id]);
  const done = todays.filter((tk) => results[tk.id]?.status === 'done').sort((a, b) => results[a.id].at.localeCompare(results[b.id].at));
  const restock = toRestock(products);
  const pendingCount = couldNot.length + open.length;
  const roomName = (roomId: string) => {
    const r = rooms.find((x) => x.id === roomId);
    return r ? pick(r.name, lang) : '';
  };

  return (
    <Screen title={t('todaysReport')} subtitle={formatLongDate(todayDate, lang)}>
      <CrewSwitch />
      {s.total === 0 ? (
        <Card style={{ paddingVertical: 20 }}>
          <Txt color={colors.textMuted} style={{ textAlign: 'center' }}>
            {t(crew === 'garden' ? 'dayOffGarden' : 'dayOff')}
          </Txt>
        </Card>
      ) : (
        <>
          <View style={{ alignSelf: 'flex-start', backgroundColor: closed ? colors.primarySoft : colors.warningSoft, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4, marginBottom: 10 }}>
            <Txt size={12} weight="semibold" color={closed ? colors.primaryDark : colors.warningText}>
              {closed ? t('reportReady') : t('inProgress')}
            </Txt>
          </View>
          <Card>
            <RowLink Icon={CircleCheck} round title={`${s.done}/${s.total}`} subtitle={t('tasksCompleted')} />
            <RowLink Icon={Clock} round title={formatMinutes(s.minutes)} subtitle={t('estimatedTime')} />
            <RowLink Icon={TriangleAlert} iconTint={colors.warning} iconBg={colors.warningSoft} round title={String(pendingCount)} subtitle={t('pending')} last />
          </Card>
          {s.open > 0 && !closed && (
            <Txt size={13} color={colors.textMuted}>
              {t('reportEndNote', { name: staff[0]?.firstName ?? '—', time: schedule.workEnd })}
            </Txt>
          )}
        </>
      )}

      {pendingCount > 0 && (
        <>
          <SectionTitle>{t('pendingTasks')}</SectionTitle>
          <Card>
            {[...couldNot, ...open].map((tk, i, arr) => {
              const r = results[tk.id];
              return (
                <View key={tk.id} style={{ flexDirection: 'row', gap: 10, paddingVertical: 12, borderBottomWidth: i === arr.length - 1 ? 0 : 1, borderBottomColor: colors.divider }}>
                  <View style={{ marginTop: 6 }}>
                    <Dot color={r ? colors.warning : colors.cardBorder} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Txt weight="medium" size={15}>
                      {pick(tk.name, lang)}
                    </Txt>
                    <Txt size={12} color={colors.textMuted}>
                      {roomName(tk.roomId)}
                    </Txt>
                    {r?.reason ? (
                      <Txt size={13} color={colors.warningText}>
                        {t('couldNotDo')}: {reasonLabels[r.reason][lang]} · {formatTime(r.at)} · {r.byName}
                      </Txt>
                    ) : (
                      <Txt size={13} color={colors.textMuted}>
                        {closed ? t('notReached') : t('notYet')}
                      </Txt>
                    )}
                    {r?.note ? (
                      <Txt size={13} style={{ fontStyle: 'italic' }}>
                        “{r.note}”
                      </Txt>
                    ) : null}
                  </View>
                </View>
              );
            })}
          </Card>
        </>
      )}

      {done.length > 0 && (
        <>
          <SectionTitle>{t('tasksCompleted').replace(/^./, (c) => c.toUpperCase())}</SectionTitle>
          <Card>
            {done.map((tk, i) => {
              const r = results[tk.id];
              return (
                <View key={tk.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 46, borderBottomWidth: i === done.length - 1 ? 0 : 1, borderBottomColor: colors.divider }}>
                  <Txt size={14} color={colors.textMuted} style={{ width: 44 }}>
                    {formatTime(r.at)}
                  </Txt>
                  <Txt size={15} style={{ flex: 1 }}>
                    {pick(tk.name, lang)}
                  </Txt>
                  <Txt size={13} color={colors.textMuted}>
                    {r.byName}
                  </Txt>
                </View>
              );
            })}
          </Card>
        </>
      )}

      {restock.length > 0 && (
        <>
          <SectionTitle>{t('productsToRestock')}</SectionTitle>
          <Card>
            {restock.map((p, i) => (
              <View key={p.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 46, borderBottomWidth: i === restock.length - 1 ? 0 : 1, borderBottomColor: colors.divider }}>
                <Dot color={statusColor[p.status]} />
                <Txt size={15} style={{ flex: 1 }}>
                  {pick(p.name, lang)}
                </Txt>
                <Txt size={13} color={statusTextColor[p.status]}>
                  {statusLabels[p.status][lang]}
                </Txt>
              </View>
            ))}
          </Card>
        </>
      )}

      {s.total > 0 && <Button label={t('exportPdf')} Icon={FileText} onPress={() => router.push({ pathname: '/report-print', params: { crew } })} style={{ marginTop: 8 }} />}

      <SectionTitle>{t('history')}</SectionTitle>
      {past.length === 0 ? (
        <Txt size={14} color={colors.textMuted}>
          {t('noHistory')}
        </Txt>
      ) : (
        <Card>
          {past.map((r, i) => (
            <RowLink
              key={`${r.date}-${r.crew}`}
              title={formatShortDate(parseDate(r.date), lang)}
              subtitle={`${r.done}/${r.total} ${t('tasksCompleted')}`}
              right={<Dot color={r.done === r.total ? colors.ok : colors.warning} />}
              onPress={() => router.push({ pathname: '/report/[date]', params: { date: r.date, crew: r.crew } })}
              last={i === past.length - 1}
            />
          ))}
        </Card>
      )}
    </Screen>
  );
}
